import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X, Expand, CalendarClock } from 'lucide-react';
import { homeSlides } from '../data/projectsData';
import { ImcPromoSlide } from './ImcPromoSlide';
import { ScheduleMeetingPanel } from './ScheduleMeetingPanel';

const INTERVAL = 10000;

export default function HomeSlideshow() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [schedule, setSchedule] = useState(false);
  const count = homeSlides.length;
  const current = homeSlides[index];

  const go = useCallback((next) => setIndex((i) => (next + count) % count), [count]);
  const reduceMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const autoplay = !paused && !lightbox && !schedule && !reduceMotion;

  useEffect(() => { setZoomed(false); }, [index]);

  useEffect(() => {
    if (!autoplay) return;
    const t = setTimeout(() => go(index + 1), INTERVAL);
    return () => clearTimeout(t);
  }, [index, autoplay, go]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      if (e.key === 'ArrowRight') go(index + 1);
      if (e.key === 'ArrowLeft') go(index - 1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [lightbox, index, go]);

  const onKey = (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
  };

  const openLightbox = () => { setZoomed(false); setLightbox(true); };

  const openSchedule = () => {
    setSchedule(true);
    setLightbox(false);
    requestAnimationFrame(() => {
      document.getElementById('schedule-a-meeting')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <section className="relative bg-[var(--mm-cream)]" aria-roledescription="carousel" aria-label="Mashmari capability highlights">
      <div
        tabIndex={0}
        onKeyDown={onKey}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        data-testid="home-slideshow"
        className="relative w-full bg-[var(--mm-cream)] focus:outline-none"
      >
        <button
          type="button"
          onClick={openLightbox}
          aria-label={`Enlarge slide: ${current.label}`}
          data-testid="home-slideshow-open-lightbox"
          className="group relative block w-full cursor-zoom-in focus:outline-none"
        >
          <div
            className="flex items-center justify-center w-full bg-[var(--mm-cream)]"
            style={{ height: 'clamp(260px, 56vw, 600px)' }}
          >
            {homeSlides.map((s, i) => (
              s.kind === 'promo' ? (
                <div
                  key={s.image}
                  data-testid={`slide-${i}`}
                  aria-hidden={i !== index}
                  className={`w-full h-full transition-opacity duration-700 ${i === index ? 'relative opacity-100' : 'absolute inset-0 opacity-0 pointer-events-none'}`}
                >
                  <ImcPromoSlide />
                </div>
              ) : (
                <img
                  key={s.image}
                  src={s.image}
                  alt={s.alt}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  data-testid={`slide-${i}`}
                  aria-hidden={i !== index}
                  className={`max-w-full max-h-full object-contain transition-opacity duration-700 ${i === index ? 'relative opacity-100' : 'absolute opacity-0 pointer-events-none'}`}
                />
              )
            ))}
          </div>
          <span className="absolute bottom-3 left-3 sm:hidden inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-[var(--mm-purple)]/90 rounded-full px-3 py-1.5">
            <Expand className="w-3 h-3" /> Tap to zoom
          </span>
        </button>

        <button
          type="button"
          onClick={openSchedule}
          data-testid="carousel-schedule-meeting"
          className="absolute bottom-3 right-3 md:bottom-5 md:right-5 z-20 inline-flex items-center gap-2 rounded-full bg-[var(--mm-purple)] hover:bg-[var(--mm-purple-mid)] text-white text-[11px] md:text-sm font-semibold px-3.5 py-2 md:px-5 md:py-2.5 shadow-lg shadow-black/25 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <CalendarClock className="w-3.5 h-3.5 md:w-4 md:h-4" />
          Schedule a Meeting
        </button>

        {/* 10s progress bar */}
        <div className="h-1 w-full bg-[var(--mm-line)]" aria-hidden="true" data-testid="home-progress-track">
          {!reduceMotion && (
            <div
              key={`${index}-${autoplay}`}
              data-testid="home-progress-bar"
              className="h-full bg-[var(--mm-purple)]"
              style={{ animation: `mmSlideProgress ${INTERVAL}ms linear forwards`, animationPlayState: autoplay ? 'running' : 'paused' }}
            />
          )}
        </div>

        <button onClick={() => go(index - 1)} aria-label="Previous slide" data-testid="slide-prev"
          className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/85 hover:bg-white text-[var(--mm-purple)] flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={() => go(index + 1)} aria-label="Next slide" data-testid="slide-next"
          className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/85 hover:bg-white text-[var(--mm-purple)] flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]">
          <ChevronRight className="w-5 h-5" />
        </button>

        <div className="absolute bottom-14 sm:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2" role="tablist" aria-label="Choose slide">
          {homeSlides.map((s, i) => (
            <button key={s.image} onClick={() => setIndex(i)} role="tab" aria-selected={i === index}
              aria-label={`Slide ${i + 1}: ${s.label}`} data-testid={`slide-dot-${i}`}
              className={`h-2 rounded-full transition-all ${i === index ? 'w-7 bg-[var(--mm-purple)]' : 'w-2 bg-[var(--mm-purple)]/30 hover:bg-[var(--mm-purple)]/55'}`} />
          ))}
        </div>
      </div>

      {schedule && <ScheduleMeetingPanel onClose={() => setSchedule(false)} />}


      {lightbox && createPortal((
        <div className="fixed inset-0 z-[9999] flex flex-col" role="dialog" aria-modal="true"
          style={{ backgroundColor: '#1c0722' }} aria-label={current.label} data-testid="home-slideshow-lightbox">
          <div className="flex items-center justify-between gap-4 px-4 md:px-6 py-3 text-white">
            <p className="text-sm font-semibold truncate">{index + 1}/{count} · {current.label}</p>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setZoomed((z) => !z)} data-testid="home-slideshow-lightbox-zoom"
                aria-label={zoomed ? 'Fit slide to screen' : 'Zoom in to actual size'}
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                {zoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
              </button>
              <button onClick={() => setLightbox(false)} data-testid="home-slideshow-lightbox-close" aria-label="Close"
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto flex items-center justify-center p-3 md:p-6" style={{ touchAction: 'pinch-zoom' }}
            data-testid="home-slideshow-lightbox-scroll">
            {current.kind === 'promo' ? (
              <div className="w-full max-w-5xl aspect-[16/7] rounded-xl overflow-hidden">
                <ImcPromoSlide />
              </div>
            ) : (
              <img
                src={current.image}
                alt={current.alt}
                onClick={() => setZoomed((z) => !z)}
                className={zoomed ? 'max-w-none w-[1600px] max-h-none cursor-zoom-out' : 'max-w-full max-h-full object-contain cursor-zoom-in'}
              />
            )}
          </div>

          <div className="flex items-center justify-center gap-4 px-4 py-4">
            <button onClick={() => go(index - 1)} aria-label="Previous slide" data-testid="home-slideshow-lightbox-prev"
              className="w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[var(--mm-purple)] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              {homeSlides.map((s, i) => (
                <button key={s.image} onClick={() => setIndex(i)} aria-label={`Slide ${i + 1}`} data-testid={`home-slideshow-lightbox-dot-${i}`}
                  className={`h-2.5 rounded-full transition-all ${i === index ? 'w-8 bg-white' : 'w-2.5 bg-white/40 hover:bg-white/70'}`} />
              ))}
            </div>
            <button onClick={() => go(index + 1)} aria-label="Next slide" data-testid="home-slideshow-lightbox-next"
              className="w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[var(--mm-purple)] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      ), document.body)}
    </section>
  );
}
